/**
 * Die Packliste als Datenstruktur — Gruppieren, Ergänzen, Streichen, Zählen.
 *
 * ## Warum diese Regeln hier stehen und nicht in der Komponente
 *
 * Weil sie ohne Browser prüfbar sein müssen. Die Packliste ist ab heute
 * **bearbeitbar**, und damit wird aus einer Anzeige ein kleiner Editor: Posten
 * kommen hinzu, werden gestrichen, wechseln ihre Marke, Bereiche werden
 * umbenannt. Jeder dieser Schritte kann still die Liste beschädigen — eine
 * doppelte Kennung, ein Posten, der beim Umbenennen seines Bereichs
 * verschwindet, ein Zähler, der Gestrichenes mitzählt. Im Browser sieht man
 * das erst, wenn eine Zeile fehlt, und dann ist sie schon gespeichert.
 *
 * Alles hier ist **unveränderlich**: Jede Funktion gibt eine neue Liste
 * zurück. Das ist kein Stilentscheid, sondern die Voraussetzung dafür, dass
 * der Blattzustand in Svelte 5 überhaupt neu zeichnet — und dafür, dass ein
 * Fehlschlag beim Speichern die alte Liste noch hat.
 *
 * ## Wer die Bedingungen rechnet
 *
 * **Der Server, nicht wir.** Welcher Posten für eine bestimmte Reise gilt,
 * steht als `aktiv` an jedem Posten und als `bedingungen` an der Antwort. Bis
 * heute rechnete der Browser das selbst (`planner/vorbereitung.packliste()`),
 * und die Regel für `camping` hing am Ergebnis des Schedulers — also an einer
 * Zahl, die es nur im Browser gibt. Eine Packliste, die im Web anders aussieht
 * als in der App, ist schlimmer als eine, die zu viel zeigt: Man packt nach
 * der, die man gerade ansieht.
 *
 * Die Funktionen hier lesen `aktiv` deshalb nur — sie leiten es nie ab.
 */

import type { Packbedingung, Packposten } from './api';

/** Die drei Bedingungen in der Reihenfolge, in der sie überall stehen. */
export const BEDINGUNGEN: readonly Packbedingung[] = ['kajak', 'camping', 'elektro'];

/** Die deutschen Namen der Bedingungen — für Chips und Sätze. */
export const BEDINGUNG_WORT: Record<Packbedingung, string> = {
  kajak: 'Kajak',
  camping: 'Camping',
  elektro: 'Elektro'
};

/* ── Freie Merkmale ───────────────────────────────────────────────────
 *
 * Seit dem 12.9.2026 hängt ein Posten nicht mehr nur an den drei eingebauten
 * Bedingungen, sondern auch an einem **freien Merkmal** der Reise (`hund`,
 * `winter`, `staedte`; Vertrag § 1.3). Der Unterschied ist einer der Herkunft,
 * nicht der Wirkung: Die drei rechnet der Server aus der Reise (Kajaktour,
 * Nacht im Fahrzeug, Antriebsakku), ein Merkmal trägt sie, weil jemand es
 * hingeschrieben hat.
 *
 * **Die Form ist eng, damit die Grenze zum Server hält**: Kleinbuchstaben,
 * ASCII, höchstens 20 Zeichen. „Hund" und „hund" wären sonst zwei Merkmale,
 * von denen eines nie greift — und das sieht man an einem Posten nicht, man
 * sieht nur, dass er fehlt.
 */

/** Die Höchstlänge eines Merkmals — dieselbe Zahl wie im Vertrag. */
export const MERKMAL_MAX = 20;

/**
 * Ein eingetipptes Merkmal in die Form des Vertrags bringen, oder `null`.
 *
 * Umlaute werden **ausgeschrieben** und nicht weggeworfen: Aus „Hündin" würde
 * sonst `h-ndin`. Dieselbe Regel wie bei den Postenkennungen (`kennung`), aus
 * demselben Grund — beides sind ASCII-Schlüssel für deutschen Text.
 *
 * `null` heißt: Daraus wird kein Merkmal. Das ist der Fall bei einer leeren
 * Eingabe und bei einer, aus der nach dem Aussieben nichts übrig bleibt.
 */
export function merkmalNormiert(text: string): string | null {
  const wert = text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MERKMAL_MAX)
    .replace(/-+$/g, '');
  return wert || null;
}

/**
 * Das Wort, das am Chip steht.
 *
 * Die drei eingebauten tragen ihren deutschen Namen, ein freies Merkmal
 * sich selbst. Kein Verschönern (`hund` → „Hund"): Das Merkmal ist ein
 * Schlüssel, und wer ihn an der Reise eintippt, soll ihn hier wiedererkennen.
 */
export function merkmalWort(m: string): string {
  return (BEDINGUNG_WORT as Record<string, string>)[m] ?? m;
}

/** Eine der drei eingebauten Bedingungen? */
export function istBedingung(m: string): m is Packbedingung {
  return (BEDINGUNGEN as readonly string[]).includes(m);
}

/**
 * Alle freien Merkmale, die auf dieser Liste vorkommen — ohne Doppelte.
 *
 * Daraus entsteht die Chipreihe am Posten: Was an **einem** Posten steht,
 * soll an jedem anderen mit einem Klick erreichbar sein. Ohne das müsste man
 * `hund` an jeder der zwölf Hundezeilen neu eintippen, und beim dritten Mal
 * entstünde `hunde`.
 *
 * Sortiert, damit die Reihe nicht von der Postenreihenfolge abhängt: Zwei
 * Listen mit denselben Merkmalen sollen dieselbe Chipreihe zeigen.
 */
export function freieMerkmale(posten: readonly Packposten[]): string[] {
  const raus = new Set<string>();
  for (const p of posten) for (const m of p.wenn ?? []) if (!istBedingung(m)) raus.add(m);
  return [...raus].sort();
}

/**
 * Die Merkmale eines Postens in der Reihenfolge, in der sie gespeichert werden.
 *
 * Erst die drei eingebauten in ihrer festen Folge, dann die freien
 * alphabetisch. Ohne diese Ordnung stünde an einem Posten `hund, camping` und
 * am nächsten `camping, hund`, und ein Vergleich zweier Listen fände einen
 * Unterschied, den es nicht gibt (derselbe Grund wie bei
 * `bedingungUmgeschaltet`).
 */
export function merkmaleSortiert(wenn: readonly string[]): string[] {
  const frei = wenn.filter((m) => !istBedingung(m));
  return [
    ...BEDINGUNGEN.filter((b) => wenn.includes(b)),
    ...[...new Set(frei)].sort()
  ];
}

/**
 * Gilt der Posten für diese Reise?
 *
 * `aktiv` fehlt in der Kontofassung der Liste (dort gibt es keine Reise, für
 * die etwas gelten könnte) — und ein fehlendes Feld heißt hier **ja**. Die
 * umgekehrte Vorgabe hätte im Konto-Editor eine leere Liste ergeben.
 */
export function istAktiv(p: Packposten): boolean {
  return p.aktiv !== false;
}

export interface Packgruppe {
  bereich: string;
  posten: Packposten[];
}

/**
 * Nach Bereich gruppieren — in der Reihenfolge des ersten Auftretens.
 *
 * Nicht alphabetisch: Die Vorlage ist nach dem Ablauf des Packens sortiert
 * (Strom, BiPAP, Schlafen, … zuletzt „Vor der Abfahrt"), und eine alphabetische
 * Liste fängt mit „Arbeiten" an und endet mit „Strom". Wer die Reihenfolge
 * ändern will, sortiert die Posten um; die Gruppierung folgt ihnen.
 */
export function gruppiere(posten: readonly Packposten[]): Packgruppe[] {
  const gruppen: Packgruppe[] = [];
  const nachName = new Map<string, Packgruppe>();
  for (const p of posten) {
    let g = nachName.get(p.bereich);
    if (!g) {
      g = { bereich: p.bereich, posten: [] };
      nachName.set(p.bereich, g);
      gruppen.push(g);
    }
    g.posten.push(p);
  }
  return gruppen;
}

/**
 * Die Liste in „gilt für diese Reise" und „gilt nicht" geteilt.
 *
 * Beide Hälften bleiben in ihrer ursprünglichen Reihenfolge. Der inaktive Teil
 * wird nicht weggeworfen: Er steht eingeklappt unter der Liste, denn er ist
 * die Antwort auf die Frage, wo die Kajaktasche hin ist — und er ist
 * bearbeitbar, ohne dass man erst eine Kajaktour einplanen muss.
 */
export function aufgeteilt(posten: readonly Packposten[]): {
  aktive: Packposten[];
  inaktive: Packposten[];
} {
  const aktive: Packposten[] = [];
  const inaktive: Packposten[] = [];
  for (const p of posten) (istAktiv(p) ? aktive : inaktive).push(p);
  return { aktive, inaktive };
}

export interface Packzaehler {
  /** Posten, die für diese Reise gelten. */
  gesamt: number;
  offen: number;
  /** Als wichtig markiert — gezählt wird der Bestand, nicht das Offene. */
  wichtig: number;
  /** Wichtig **und** offen. Das ist die Zahl, die Handlungsbedarf auslöst. */
  kritischOffen: number;
}

/**
 * Die Zahlen für die Kopfzeile.
 *
 * Gezählt wird **nur**, was für diese Reise gilt. Ein Zähler, der die
 * eingeklappten Posten mitzählte, stünde dauerhaft auf einer Zahl, die niemand
 * kleiner bekommt — dieselbe Überlegung wie bei den Höhenmetern im
 * Vorbereitungsreiter, die bewusst keinen Handlungsbedarf erzeugen.
 */
export function zaehler(posten: readonly Packposten[]): Packzaehler {
  const aktive = posten.filter(istAktiv);
  return {
    gesamt: aktive.length,
    offen: aktive.filter((p) => !p.erledigt).length,
    wichtig: aktive.filter((p) => p.kritisch).length,
    kritischOffen: aktive.filter((p) => p.kritisch && !p.erledigt).length
  };
}

/**
 * Die Kopfzeile des Abschnitts: `3 von 12 offen · 2 wichtig`.
 *
 * Eine leere Liste sagt das auch so. Vorher hätte sie „vollständig" gesagt —
 * dasselbe Wort wie eine abgehakte Liste, und das ist der Unterschied zwischen
 * „alles dabei" und „noch nichts drauf".
 */
export function standtext(z: Packzaehler): string {
  if (z.gesamt === 0) return 'noch kein Posten';
  const offen = z.offen === 0 ? 'vollständig' : `${z.offen} von ${z.gesamt} offen`;
  return z.kritischOffen > 0 ? `${offen} · ${z.kritischOffen} wichtig` : offen;
}

/**
 * Der Kopf des Packlisten-Abschnitts im Vorbereitungsreiter.
 *
 * Steht hier und nicht in der Komponente, weil an ihm der **Handlungsbedarf
 * des ganzen Reiters** hängt: `Panelkopf art="checkliste"` zählt die
 * Abschnitte mit Bedarf, und die Packliste ist einer davon. Ein Zähler, der
 * während des Ladens eine Null behauptet, machte aus einer unbekannten Liste
 * eine erledigte — dieselbe Ehrlichkeit, die im Prüflauf „noch nicht geprüft"
 * von „nichts offen" unterscheidet.
 *
 * `ton` und `handlungsbedarf` sagen zwei verschiedene Dinge, und das ist
 * Absicht: Offen ist offen (auch fünf Sonnencremes), aber die Farbe springt
 * erst auf `ernst`, wenn etwas Wichtiges offen ist.
 */
export interface Packabschnitt {
  stand: string;
  ton: 'ruhig' | 'merk' | 'ernst';
  handlungsbedarf: boolean;
}

export function abschnittsstand(opt: {
  laeuft: boolean;
  fehler: boolean;
  zahlen: Packzaehler;
}): Packabschnitt {
  const { laeuft, fehler, zahlen } = opt;
  return {
    stand: laeuft ? 'lädt …' : fehler ? 'nicht abrufbar' : standtext(zahlen),
    ton: zahlen.kritischOffen ? 'ernst' : zahlen.offen ? 'merk' : 'ruhig',
    // Während des Ladens und im Fehlerfall kein Bedarf: Beides ist eine
    // Aussage über die Verbindung, nicht über die Packliste.
    handlungsbedarf: !laeuft && !fehler && zahlen.offen > 0
  };
}

/** Die vorhandenen Bereiche, ohne Doppelte, in Listenreihenfolge. */
export function bereiche(posten: readonly Packposten[]): string[] {
  const out: string[] = [];
  for (const p of posten) if (!out.includes(p.bereich)) out.push(p.bereich);
  return out;
}

/**
 * Eine Kennung aus dem Text des Postens.
 *
 * ## Warum kein Zufall und keine Uhr
 *
 * Weil beides diese Datei untestbar machte und weil eine sprechende Kennung im
 * Fehlerfall die nützlichere ist: Steht in einem Protokoll `strom-v2l-adapter`,
 * weiß man, welche Zeile gemeint ist. `crypto.randomUUID()` sagt darüber
 * nichts.
 *
 * Kollisionen werden durchgezählt (`-2`, `-3`) und gegen die **vorhandenen**
 * Kennungen geprüft, nicht gegen den Bereich: Derselbe Text kann in zwei
 * Bereichen stehen (`Sonstiges/Stirnlampe` und `Kajak/Stirnlampe`), und zwei
 * Posten mit derselben Kennung wären beim Abhaken nicht zu unterscheiden.
 *
 * Der Rückfall `posten` greift bei einem Text, aus dem nichts übrig bleibt —
 * ein Name aus reinen Satzzeichen. Ohne ihn hieße der Posten `-2`.
 */
export function neueId(was: string, vorhanden: Iterable<string> = []): string {
  const belegt = new Set(vorhanden);
  const kern = kennung(was) || 'posten';
  if (!belegt.has(kern)) return kern;
  for (let n = 2; ; n++) {
    const versuch = `${kern}-${n}`;
    if (!belegt.has(versuch)) return versuch;
  }
}

/**
 * Text zu Kennung: Kleinbuchstaben, Umlaute ausgeschrieben, Rest zu Strichen.
 *
 * Umlaute **ausgeschrieben** und nicht weggeworfen: Aus `Sanitär` würde sonst
 * `sanit-r`, aus `Verlängerung` `verl-ngerung`. Beides ist lesbar falsch.
 */
function kennung(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
}

export interface NeuerPosten {
  was: string;
  bereich: string;
  kritisch?: boolean;
  /** Bedingungen **und** freie Merkmale — siehe `Packposten.wenn`. */
  wenn?: string[] | null;
}

/**
 * Einen Posten ergänzen — hinter den letzten seines Bereichs.
 *
 * **Nicht ans Ende der Liste.** Wer in „Küche" etwas nachträgt, will es in der
 * Küche wiederfinden; landete es am Ende, entstünde unter „Vor der Abfahrt"
 * eine zweite Küchengruppe (`gruppiere` fasst zwar zusammen, aber die
 * Reihenfolge innerhalb der Liste bliebe zerrissen, und das nächste Speichern
 * schriebe sie so fest).
 *
 * Ein neuer Bereich kommt ans Ende — er hat noch keinen Ort.
 *
 * `aktiv: true` und nicht „was der Server sagen wird": Der Posten ist gerade
 * für diese Reise angelegt worden. Beim nächsten GET rechnet der Server es neu
 * und darf widersprechen; bis dahin wäre ein gedimmter, eingeklappter Posten
 * die falschere Antwort auf einen Klick.
 */
export function ergaenzt(posten: readonly Packposten[], neu: NeuerPosten): Packposten[] {
  const eintrag: Packposten = {
    id: neueId(neu.was, posten.map((p) => p.id)),
    bereich: neu.bereich.trim(),
    was: neu.was.trim(),
    kritisch: neu.kritisch ?? false,
    wenn: neu.wenn ?? null,
    erledigt: false,
    notiz: null,
    aktiv: true
  };
  const letzter = letzteStelle(posten, eintrag.bereich);
  if (letzter < 0) return [...posten, eintrag];
  return [...posten.slice(0, letzter + 1), eintrag, ...posten.slice(letzter + 1)];
}

/** Die Stelle des letzten Postens dieses Bereichs, oder -1. */
function letzteStelle(posten: readonly Packposten[], bereich: string): number {
  for (let i = posten.length - 1; i >= 0; i--) if (posten[i].bereich === bereich) return i;
  return -1;
}

/** Einen Posten streichen. Unbekannte Kennung lässt die Liste, wie sie ist. */
export function gestrichen(posten: readonly Packposten[], id: string): Packposten[] {
  return posten.filter((p) => p.id !== id);
}

/**
 * Einen gestrichenen Posten wieder einsetzen — an seiner alten Stelle.
 *
 * Das ist die ganze Rückgängig-Möglichkeit des Blattes, und sie ist bewusst
 * nicht die Historie des Planers: Die gehört den Stationen, und ein Strg+Z
 * nach dem Streichen einer Packzeile soll die zuletzt verschobene Station
 * zurückholen (dieselbe Abwägung wie bei den Maut-Vignetten, siehe
 * `Planner.vignettenSetzen`).
 *
 * `stelle` wird geklemmt: Wer zwischendurch andere Posten gestrichen hat,
 * bekommt den seinen ans Ende statt eine Lücke oder einen Fehler.
 */
export function eingesetzt(
  posten: readonly Packposten[],
  eintrag: Packposten,
  stelle: number
): Packposten[] {
  if (posten.some((p) => p.id === eintrag.id)) return [...posten];
  const i = Math.max(0, Math.min(stelle, posten.length));
  return [...posten.slice(0, i), eintrag, ...posten.slice(i)];
}

/**
 * Ein Feld eines Postens ändern.
 *
 * `id` und `aktiv` sind ausgenommen: Die Kennung ist der Griff, über den diese
 * Funktion überhaupt arbeitet, und `aktiv` ist die Antwort des Servers auf
 * eine Frage, die der Browser nicht stellt.
 */
export function geaendert(
  posten: readonly Packposten[],
  id: string,
  patch: Partial<Omit<Packposten, 'id' | 'aktiv'>>
): Packposten[] {
  return posten.map((p) => (p.id === id ? { ...p, ...patch } : p));
}

/**
 * Eine Bedingung an einem Posten umschalten.
 *
 * Die Leerliste wird zu `null` — beides hieße „gilt immer", und zwei
 * Schreibweisen für dieselbe Sache laufen an der Grenze zum Server
 * auseinander. `null` ist die im Vertrag.
 *
 * Sortiert wird nach `BEDINGUNGEN` und nicht nach Klickreihenfolge: Sonst
 * stünde an einem Posten `elektro, camping` und am nächsten `camping,
 * elektro`, und ein Vergleich zweier Listen (etwa gegen den Standard) fände
 * einen Unterschied, den es nicht gibt.
 */
export function bedingungUmgeschaltet(
  posten: readonly Packposten[],
  id: string,
  b: string
): Packposten[] {
  return posten.map((p) => {
    if (p.id !== id) return p;
    const jetzt = p.wenn ?? [];
    const neu = jetzt.includes(b) ? jetzt.filter((x) => x !== b) : [...jetzt, b];
    const sortiert = merkmaleSortiert(neu);
    return { ...p, wenn: sortiert.length ? sortiert : null };
  });
}

/**
 * Ein **frisch eingetipptes** Merkmal an einen Posten hängen.
 *
 * Zwei Schritte in einem, weil sie zusammengehören: normieren (`Hund` →
 * `hund`) und dann umschalten. Getrennt aufgerufen entstünde beim zweiten Mal
 * ein zweites Merkmal, das sich nur in der Schreibweise unterscheidet.
 *
 * Gibt die Liste **unverändert** zurück, wenn aus der Eingabe kein Merkmal
 * wird (leer, nur Satzzeichen) — und der Aufrufer sieht das daran, dass
 * `merkmal` `null` ist. Ein stilles Nichts wäre an einem Knopf die
 * unangenehmere Antwort.
 */
export function merkmalErgaenzt(
  posten: readonly Packposten[],
  id: string,
  eingabe: string
): { posten: Packposten[]; merkmal: string | null } {
  const merkmal = merkmalNormiert(eingabe);
  if (!merkmal) return { posten: [...posten], merkmal: null };
  const jetzt = posten.find((p) => p.id === id)?.wenn ?? [];
  // Schon dran: dann ist der Wunsch erfüllt, und ein Umschalten nähme es weg.
  if (jetzt.includes(merkmal)) return { posten: [...posten], merkmal };
  return { posten: bedingungUmgeschaltet(posten, id, merkmal), merkmal };
}

/**
 * Einen Bereich umbenennen.
 *
 * Läuft der neue Name auf einen vorhandenen, verschmelzen die beiden Gruppen —
 * das ist gewollt und der bequemste Weg, einen versehentlich doppelt
 * angelegten Bereich (`Küche` und `Kueche`) wieder zusammenzuführen. Ein
 * leerer neuer Name lässt alles stehen: Ein Bereich ohne Namen wäre eine
 * Gruppe ohne Überschrift.
 */
export function bereichUmbenannt(
  posten: readonly Packposten[],
  alt: string,
  neu: string
): Packposten[] {
  const name = neu.trim();
  if (!name) return [...posten];
  return posten.map((p) => (p.bereich === alt ? { ...p, bereich: name } : p));
}

/**
 * Die eingebaute Vorlage in die Form des Vertrags bringen — der **Rückfall**.
 *
 * Gebraucht wird das für genau einen Fall: Der Server kennt die Packliste noch
 * nicht (404 oder eine alte Fassung). Dann steht die eingebaute Liste da,
 * vollständig und **nur lesend**, mit einem Satz darüber. Die Alternative wäre
 * ein leerer Abschnitt gewesen — und ein leerer Abschnitt sieht aus wie „nichts
 * zu packen".
 *
 * **Ungefiltert**, obwohl die Vorlage ihre Bedingungen mitbringt. Welcher
 * Posten für eine Reise gilt, entscheidet ab heute der Server; die Regel hier
 * noch einmal nachzubauen, um sie für zwei Wochen zu benutzen, wäre genau die
 * zweite Wahrheit, deren Beseitigung der Sinn der Umstellung ist. Der Hinweis
 * über der Liste sagt das auch so.
 *
 * `wenn: 'immer'` der Vorlage wird zu `null` — im Vertrag ist das dieselbe
 * Aussage, und sie braucht kein eigenes Wort.
 */
export function rueckfallposten(
  vorlage: readonly {
    bereich: string;
    was: string;
    kritisch?: boolean;
    wenn?: Packbedingung | 'immer' | (Packbedingung | 'immer')[];
  }[]
): Packposten[] {
  const ids: string[] = [];
  return vorlage.map((v) => {
    const id = neueId(v.was, ids);
    ids.push(id);
    const roh = v.wenn === undefined ? [] : Array.isArray(v.wenn) ? v.wenn : [v.wenn];
    const wenn = BEDINGUNGEN.filter((b) => roh.includes(b));
    return {
      id,
      bereich: v.bereich,
      was: v.was,
      kritisch: v.kritisch ?? false,
      wenn: wenn.length ? [...wenn] : null,
      erledigt: false,
      notiz: null,
      aktiv: true
    };
  });
}
